import { ViewConfig } from "@lib/views";
import type { ApplyCloseTemplateEntitySelectionRow } from "./applyCloseTemplateEntitySelection.types";
import { ApplyCloseEntityClientCell } from "./cells/ApplyCloseEntityClientCell";
import { ApplyCloseEntityEndDateCell } from "./cells/ApplyCloseEntityEndDateCell";
import { ApplyCloseEntityIndustriesCell } from "./cells/ApplyCloseEntityIndustriesCell";
import { ApplyCloseEntityLegalTypeCell } from "./cells/ApplyCloseEntityLegalTypeCell";
import { ApplyCloseEntityNameCell } from "./cells/ApplyCloseEntityNameCell";
import { ApplyCloseEntityManagerCell } from "./cells/ApplyCloseEntityManagerCell";
import { ApplyCloseEntityPreparerCell } from "./cells/ApplyCloseEntityPreparerCell";
import { ApplyCloseEntityReviewerCell } from "./cells/ApplyCloseEntityReviewerCell";
import { ApplyCloseEntityStartDateCell } from "./cells/ApplyCloseEntityStartDateCell";
import { ApplyCloseEntityWorkStartDateCell } from "./cells/ApplyCloseEntityWorkStartDateCell";
import { ApplyCloseEntityWorkEndDateCell } from "./cells/ApplyCloseEntityWorkEndDateCell";
import { ApplyCloseEntityPlanCell } from "./cells/ApplyCloseEntityPlanCell";
import { ApplyCloseEntitySelectCell } from "./cells/ApplyCloseEntitySelectCell";

export enum ApplyCloseTemplateEntityColumn {
    NAME = "name",
    INDUSTRIES = "industries",
    LEGAL_TYPE = "legalType",
    // Column id kept as START_DATE/END_DATE for backward-compat with any
    // persisted view state; per PRO-10838 the user-facing label is now
    // "Service start"/"Service end" (this is the close/work-period service
    // window, distinct from the work start/end dates below).
    START_DATE = "startDate",
    END_DATE = "endDate",
    WORK_START = "workStartDate",
    WORK_END = "workDeliveryDate",
    PREPARER = "preparer",
    REVIEWER = "reviewer",
    MANAGER = "manager",
    CLIENT = "client",
    PLAN = "plan",
    SELECT = "applyToSelect",
}

export const applyCloseTemplateEntitySelectionViewConfig: ViewConfig<
    ApplyCloseTemplateEntitySelectionRow,
    ApplyCloseTemplateEntityColumn,
    never
> = {
    columns: {
        [ApplyCloseTemplateEntityColumn.NAME]: {
            accessorKey: "name",
            name: "Client / entity",
            header: "Client / entity",
            cell: ApplyCloseEntityNameCell,
            immutable: true,
            minSize: 260,
            maxSize: 480,
        },
        [ApplyCloseTemplateEntityColumn.START_DATE]: {
            accessorKey: "startDate",
            name: "Service start",
            header: "Service start",
            cell: ApplyCloseEntityStartDateCell,
            minSize: 160,
            maxSize: 160,
        },
        [ApplyCloseTemplateEntityColumn.END_DATE]: {
            accessorKey: "endDate",
            name: "Service end",
            header: "Service end",
            cell: ApplyCloseEntityEndDateCell,
            minSize: 160,
            maxSize: 160,
        },
        [ApplyCloseTemplateEntityColumn.WORK_START]: {
            accessorKey: "workStartDate",
            name: "Work start",
            header: "Work start",
            cell: ApplyCloseEntityWorkStartDateCell,
            minSize: 160,
            maxSize: 160,
        },
        [ApplyCloseTemplateEntityColumn.WORK_END]: {
            accessorKey: "workDeliveryDate",
            name: "Work end",
            header: "Work end",
            cell: ApplyCloseEntityWorkEndDateCell,
            minSize: 160,
            maxSize: 160,
        },
        [ApplyCloseTemplateEntityColumn.PREPARER]: {
            accessorKey: "preparerId",
            name: "Preparer",
            header: "Preparer",
            cell: ApplyCloseEntityPreparerCell,
            minSize: 220,
            maxSize: 320,
        },
        [ApplyCloseTemplateEntityColumn.INDUSTRIES]: {
            accessorKey: "industries",
            name: "Industries",
            header: "Industries",
            cell: ApplyCloseEntityIndustriesCell,
            minSize: 140,
            maxSize: 280,
        },
        [ApplyCloseTemplateEntityColumn.LEGAL_TYPE]: {
            accessorKey: "legalType",
            name: "Legal type",
            header: "Legal type",
            cell: ApplyCloseEntityLegalTypeCell,
            minSize: 140,
            maxSize: 240,
        },
        [ApplyCloseTemplateEntityColumn.REVIEWER]: {
            accessorKey: "reviewerId",
            name: "Reviewer",
            header: "Reviewer",
            cell: ApplyCloseEntityReviewerCell,
            minSize: 220,
            maxSize: 320,
        },
        [ApplyCloseTemplateEntityColumn.MANAGER]: {
            accessorKey: "managerId",
            name: "Manager",
            header: "Manager",
            cell: ApplyCloseEntityManagerCell,
            minSize: 220,
            maxSize: 320,
        },
        [ApplyCloseTemplateEntityColumn.CLIENT]: {
            accessorKey: "clientId",
            name: "Client",
            header: "Client",
            cell: ApplyCloseEntityClientCell,
            minSize: 220,
            maxSize: 320,
        },
        [ApplyCloseTemplateEntityColumn.PLAN]: {
            accessorKey: "plan",
            name: "Plan",
            header: "Plan",
            cell: ApplyCloseEntityPlanCell,
            minSize: 120,
            maxSize: 200,
        },
        [ApplyCloseTemplateEntityColumn.SELECT]: {
            accessorKey: "id",
            name: "",
            header: "",
            cell: ApplyCloseEntitySelectCell,
            minSize: 110,
            maxSize: 110,
            internal: true,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: ApplyCloseTemplateEntityColumn.NAME,
        childrenKey: "children",
    },
};
